import { zodResolver } from "@hookform/resolvers/zod";
import { Grid, FormHelperText } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { Job } from "generated/graphql";
import useUpdateJobExternalId from "hooks/jobs/useUpdateJobExternalId";
import useModalState from "hooks/useModalState";
import Modal from "modules/Modal/Modal";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { IconButton } from "@mui/material";
import { Edit } from "@mui/icons-material";

const schema = z.object({
  externalId: z.string().min(1, { message: "Job number is required" }),
});

type FormData = z.infer<typeof schema>;

export default function EditJobExternalIdModal({
  jobId,
  currentExternalId,
}: {
  jobId: Job["id"];
  currentExternalId: Job["externalId"];
}) {
  const { open, onClose, onOpen } = useModalState();
  const [updateJobExternalId, { loading }] = useUpdateJobExternalId();

  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: { externalId: currentExternalId || "" },
    resolver: zodResolver(schema),
  });

  const handleOpen = () => {
    reset({ externalId: currentExternalId || "" });
    onOpen();
  };

  const onSubmit = async (data: FormData) => {
    await updateJobExternalId({
      variables: {
        id: jobId,
        externalId: data.externalId,
      },
    });
    onClose();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen} sx={{ ml: 1 }}>
        <Edit fontSize="small" />
      </IconButton>

      <Modal open={open} onClose={onClose} styles={{ width: "500px", height: "auto" }}>
        <MDBox p={3}>
          <MDTypography variant="h5" fontWeight="medium" mb={3}>
            Edit Job Number
          </MDTypography>

          <MDBox component="form" role="form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <MDInput
                  label="Job Number"
                  fullWidth
                  error={!!errors.externalId}
                  {...register("externalId")}
                />
                {errors.externalId && (
                  <FormHelperText sx={{ color: "red" }}>{errors.externalId.message}</FormHelperText>
                )}
              </Grid>

              <Grid item xs={12}>
                <MDBox display="flex" justifyContent="flex-end" gap={2}>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </MDButton>
                  <MDButton type="submit" variant="gradient" color="success" disabled={loading}>
                    Save
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </MDBox>
      </Modal>
    </>
  );
}
